import React, {useMemo, useState, useRef, useEffect} from "react";
import {ExtractorProps, JobUpload, PersistedExtractionJob, SubtitleBurnInJob} from "@/types";
import Select from "@/Components/Select";
import Popover from "@/Components/Popover";
import ProgressBar from "@/Components/ProgressBar";
import PrimaryButton from "@/Components/PrimaryButton";
import Spinner from "@/Components/Spinner";
import {formatMinutes, getLanguageName, bytesToSize, secondsToUnits, secondsToDuration} from "@/utils/textFormatting";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFilm, faFileExport, faArrowUpFromBracket,faInfoCircle, faArrowRight, faRightLeft, faClosedCaptioning, faFireFlameCurved} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import axios from "axios";
import toast from "react-hot-toast";
import {VideoPlayerRef} from "@/Pages/Subtitle/Partials/VideoPlayer";
import PurchaseModal from "@/Pages/Dashboard/Purchase/PurchaseModal";
import InsufficientCreditsException from "@/exceptions/InsufficientCreditsException";
import {usePage} from "@inertiajs/react";
import uploadFile, {finalizeUpload} from "@/services/localFileUploader";
import mediaFileCacheService from "@/services/MediaFileCacheService";
import {v4 as uuidv4} from "uuid";
import useSoftcodeExport from "@/hooks/useSoftcodeExport";
import {burnInSubtitle, downloadOutput, listenForBurnInJobUpdates} from "@/services/subtitleBurnInJobManager";
import {useConfirm} from "@/Components/ConfirmDialog";
import InfoIcon from "@/Components/Icons/InfoIcon";
import {getSubtitleData, updateSubtitleData} from "@/services/subtitleService";
import {SubtitleData} from "@/models/SubtitleData";
import {initializeSubtitleFonts} from "@/services/subtitleFontService";
import {AnalyticsEvent, sendEvent} from "@/services/analytics";

type VideoExportTabProps = {
    job: PersistedExtractionJob;
    show: boolean;
    handleCloseRef: React.MutableRefObject<(() => Promise<boolean>) | null>;
    languageOptions: Record<number, string>;
    defaultLanguage?: string;
    initialUpload: JobUpload | null;
    videoPlayerRef: VideoPlayerRef;
    isFreeTrial: boolean;
    onClose: () => void;
    onBurnInStart?: (job: SubtitleBurnInJob) => void;
    onUploadComplete: () => void;
}

type ExportView = 'options' | 'upload_progress' | 'confirmation' | 'progress';
type EmbedType = 'hardcode' | 'softcode';

export default function VideoExportTab({
    job,
    show,
    handleCloseRef,
    languageOptions,
    defaultLanguage,
    initialUpload,
    videoPlayerRef,
    isFreeTrial,
    onClose,
    onBurnInStart,
    onUploadComplete
}: VideoExportTabProps) {
    const uploadParams = usePage<{extractorParams: ExtractorProps}>().props.extractorParams;
    const confirm = useConfirm();
    const [selectedLanguage, setSelectedLanguage] = useState<string>(defaultLanguage || '');
    const [embedType, setEmbedType] = useState<EmbedType>('hardcode');
    const [currentView, setCurrentView] = useState<ExportView>('options');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadEta, setUploadEta] = useState<number | undefined>(undefined);
    const uploadAbortController = useRef<AbortController | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
    const [creditBalance, setCreditBalance] = useState(0);
    const [upload, setUpload] = useState<JobUpload | null>(initialUpload);
    const [videoDurationSeconds, setVideoDurationSeconds] = useState(initialUpload?.media_duration || videoPlayerRef.duration);
    const [burnInProgress, setBurnInProgress] = useState(0);
    const [withStyles, setWithStyles] = useState(true);
    const burnInListenerRef = useRef<{ disconnect: () => void } | null>(null);

    const estimatedProcessingDuration = useMemo(() => {
        const durationMinutes = videoDurationSeconds / 60;
        if (durationMinutes <= 5) {
            return 3;
        }

        if (durationMinutes <= 200) {
            return 6;
        }

        const epocs = Math.ceil(durationMinutes / 200);
        return 6 * epocs;
    }, [videoDurationSeconds])

    const viewRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [viewHeights, setViewHeights] = useState<number[]>([]);
    const observerRef = useRef<ResizeObserver | null>(null);

    const currentMediaFile = videoPlayerRef.mediaFile;

    const softcode = useSoftcodeExport();

    useEffect(() => {
        handleCloseRef.current = async () => {
            if (isUploading) {
                const confirmed = await confirm("Are you sure you want to cancel the video upload?");
                if (confirmed) {
                    uploadAbortController.current?.abort();
                }
                return confirmed;
            }

            if (currentView === 'progress' && embedType === 'softcode') {
                const confirmed = await confirm("Are you sure you want to cancel the export?");
                if (confirmed) {
                    softcode.handleCancel();
                }
                return confirmed;
            }

            return true;
        };

        return () => {
            handleCloseRef.current = null;
        };
    }, [isUploading, currentView, embedType, softcode]);

    useEffect(() => {
        return () => {
            burnInListenerRef.current?.disconnect();
        };
    }, []);

    const hasVideoUpload = useMemo(() => {
        return upload?.media_type === 'video' &&
            moment(upload.expires_at).isAfter(moment().add(1, 'minute'));
    }, [upload]);

    const burnInRate = laravelConfig<number>('pricing.credit_rates.burn_in', 1);
    const creditCost = embedType === 'softcode' ? 0 : videoDurationSeconds / 60 * burnInRate;
    const canUpload = !isFreeTrial && !isUploading && currentMediaFile !== null;

    const handleContinueToUpload = () => {
        if (!hasVideoUpload) {
            setCurrentView('upload_progress');
            handleUpload();
        } else {
            setCurrentView('confirmation');
        }
    };

    const handleCancelUpload = () => {
        if (uploadAbortController.current) {
            uploadAbortController.current.abort();
        }
        setIsUploading(false);
        setUploadProgress(0);
        setUploadEta(undefined);
        setCurrentView('options');
    };

    const handleUpload = async () => {
        if (!currentMediaFile) return;

        uploadAbortController.current = new AbortController();
        const signal = uploadAbortController.current.signal;
        setIsUploading(true);
        setUploadProgress(0);
        setUploadEta(undefined);

        const uploadId = job.upload_id ?? uuidv4();

        try {
            await uploadFile({
                file: currentMediaFile,
                name: uploadId,
                partSize: uploadParams.maxFilePartSize,
                maxConcurrentParts: uploadParams.maxConcurrentPartUploads,
                signal,
                mediaType: 'video',
                progressCallback: (percentage, secondsLeft) => {
                    if (!signal.aborted) {
                        setUploadProgress(percentage);
                        if (secondsLeft !== undefined) {
                            setUploadEta(secondsLeft);
                        }
                    }
                }
            });

            try {
                const upload = await finalizeUpload(uploadId, job.id)
                void mediaFileCacheService.saveFile(uploadId, currentMediaFile);
                setUpload(upload);
                setVideoDurationSeconds(upload.media_duration);
                onUploadComplete();
                setCurrentView('confirmation');
            } catch (error: any) {
                toast.error(error.message || 'Upload failed');
                setCurrentView('options');
            }
        } catch (e: any) {
            if (signal.aborted) {
                return;
            }
            const message = e?.message || e;
            if (typeof message === 'string' && message.includes('aborted')) {
                return;
            }
            toast.error('Upload failed');
            setCurrentView('options');
        }
        setIsUploading(false);
        setUploadEta(undefined);
    };

    const isDurationMismatch = (): boolean => {
        const originalDuration = upload?.media_duration || 0;
        const currentDuration = videoPlayerRef.duration;
        const difference = Math.abs(originalDuration - currentDuration);
        const percentDiff = (difference / Math.max(originalDuration, currentDuration)) * 100;
        return difference > 5 || percentDiff > 5;
    };

    const ensureFontsInitialized = async (language: string) => {
        if (language === defaultLanguage) return;

        const videoHeight = videoPlayerRef.videoDimensions?.height || 1080;
        const videoWidth = videoPlayerRef.videoDimensions?.width || 1920;
        const data = await getSubtitleData(job.id, language);

        if (!data.style?.fontFamily) {
            const {subtitles: initialized} = await initializeSubtitleFonts(data.subtitles, videoHeight);
            const subtitleData = new SubtitleData({
                subtitles: initialized,
                metadata: {videoWidth, videoHeight}
            });
            await updateSubtitleData(job.id, subtitleData, language);
        }
    };

    const handleExport = async () => {
        if(videoDurationSeconds === 0) {
            return toast.error('Video duration is 0, invalid video');
        }

        if (embedType === 'softcode') {
            if (!currentMediaFile) return;

            setCurrentView('progress');
            sendEvent(AnalyticsEvent.VIDEO_EXPORT_SOFTCODE, {
                language_code: selectedLanguage,
            });

            try {
                await softcode.handleExport({
                    jobId: job.id,
                    jobName: job.job_name,
                    fileName: job.file_name,
                    language: selectedLanguage,
                    withStyles: withStyles,
                    mediaFile: currentMediaFile,
                    videoDimensions: videoPlayerRef.videoDimensions,
                    onSuccess: () => {
                        toast.success('Video exported successfully');
                        onClose();
                    }
                });
            } catch (error: any) {
                toast.error(error.message || 'Export failed');
                setCurrentView('options');
            }
            return;
        }

        if (isDurationMismatch()) {
            const confirmed = await confirm(
                `The preview video doesn't match original video used for subtitle extraction.
                Export will burn subtitles into the original. Continue?`
            );
            if (!confirmed) {
                return;
            }
        }

        setIsSubmitting(true);

        try {
            const subtitles = Object.keys(languageOptions).map(Number)
            const subtitleId = subtitles.find(lang => languageOptions[lang] === selectedLanguage)!;

            await ensureFontsInitialized(selectedLanguage);

            const burnInJob = await burnInSubtitle(subtitleId);

            sendEvent(AnalyticsEvent.VIDEO_EXPORT_HARDCODE, {
                language_code: selectedLanguage,
            });

            setCurrentView('progress');
            setBurnInProgress(0);
            onBurnInStart?.(burnInJob);

            burnInListenerRef.current = listenForBurnInJobUpdates({
                jobId: burnInJob.job_name,
                onUpdate: (progress) => {
                    setBurnInProgress(progress / 100);
                },
                onError: (error) => {
                    toast.error(error.message || 'Export failed');
                    setCurrentView('confirmation');
                    burnInListenerRef.current?.disconnect();
                    burnInListenerRef.current = null;
                },
                onComplete: () => {
                    toast.success('Video exported successfully');
                    downloadOutput(burnInJob.id)
                    onClose();
                    burnInListenerRef.current?.disconnect();
                    burnInListenerRef.current = null;
                }
            });
        } catch (error: any) {
            if (error instanceof InsufficientCreditsException) {
                setCreditBalance(error.currentBalance);
                setShowCreditPurchaseModal(true);
            } else {
                toast.error(error.message || 'Export failed');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const languageSelectOptions = useMemo(() => {
        return Object.values(languageOptions).map(lang => ({
            label: getLanguageName(lang),
            value: lang
        }));
    }, [languageOptions]);

    useEffect(() => {
        const updateHeight = (index: number) => {
            const viewElement = viewRefs.current[index];
            if (viewElement) {
                setViewHeights(prevHeights => {
                    const newHeights = [...prevHeights];
                    newHeights[index] = viewElement.scrollHeight;
                    return newHeights;
                });
            }
        };

        observerRef.current = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                const index = viewRefs.current.findIndex(ref => ref === entry.target);
                if (index !== -1) {
                    updateHeight(index);
                }
            });
        });

        viewRefs.current.forEach((viewRef, index) => {
            if (viewRef) {
                observerRef.current?.observe(viewRef);
                updateHeight(index);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    const getViewIndex = () => {
        switch (currentView) {
            case 'options': return 0;
            case 'upload_progress': return 1;
            case 'confirmation': return 2;
            case 'progress': return 3;
            default: return 0;
        }
    };

    if(!show) {
        return null;
    }

    return (
        <>
            <PurchaseModal
                show={showCreditPurchaseModal}
                onCompleted={async () => {
                    setShowCreditPurchaseModal(false);
                    await handleExport();
                }}
                message={`You have ${formatMinutes(creditBalance)} of processing credits remaining, but need ${formatMinutes(creditCost)} to export this video.`}
                onClose={() => setShowCreditPurchaseModal(false)}
            />
            <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ height: viewHeights[getViewIndex()] ? `${viewHeights[getViewIndex()]}px` : 'auto' }}
            >
                <div
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{ transform: `translateX(-${getViewIndex() * 100}%)` }}
                >
                    {/* View 0: Options View */}
                    <div
                        ref={el => viewRefs.current[0] = el}
                        className="w-full flex-shrink-0 px-7 pt-5 pb-5 transition-opacity duration-300"
                        style={{ opacity: currentView === 'options' ? 1 : 0 }}
                    >
                        {/* Embed Type Toggle */}

                        <div className="mb-5">
                            <div className="flex gap-3">
                                <Popover
                                    delay={200}
                                    trigger="hover"
                                    placement="top"
                                    portal={true}
                                    content={
                                        <div className="w-64 text-sm text-gray-500 dark:text-gray-100">
                                            <div className="px-3 py-2">
                                                <p>Subtitles are permanently burned into the video. Cannot be turned off but works with all video players.</p>
                                            </div>
                                        </div>
                                    }
                                >
                                    <button
                                        onClick={() => setEmbedType('hardcode')}
                                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                                            embedType === 'hardcode'
                                                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 text-gray-900 dark:text-gray-100'
                                        }`}
                                    >
                                        <div className="text-sm font-bold mb-0.5">Hardcode</div>
                                        <div className={`text-xs ${embedType === 'hardcode' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>Burn subtitles into video permanently</div>
                                    </button>
                                </Popover>
                                <Popover
                                    delay={200}
                                    trigger="hover"
                                    placement="top"
                                    portal={true}
                                    content={
                                        <div className="w-64 text-sm text-gray-500 dark:text-gray-100">
                                            <div className="px-3 py-2">
                                                <p>Subtitles are embedded as a separate track in the video file. Can be toggled on/off in most modern video players.</p>
                                            </div>
                                        </div>
                                    }
                                >
                                    <button
                                        onClick={() => setEmbedType('softcode')}
                                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                                            embedType === 'softcode'
                                                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 text-gray-900 dark:text-gray-100'
                                        }`}
                                    >

                                        <div className="text-sm font-bold mb-0.5">Softcode</div>
                                        <div className={`text-xs ${embedType === 'softcode' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>Embed toggleable subtitle track</div>
                                    </button>
                                </Popover>
                            </div>
                        </div>

                        {/* Language Selector */}
                        <div className="mb-5">
                            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                Subtitle Language
                            </label>
                            <Select
                                variant="modal"
                                classNames={{
                                    control: () => '!rounded-lg border font-medium text-sm'
                                }}
                                value={selectedLanguage ? {label: getLanguageName(selectedLanguage), value: selectedLanguage} : null}
                                onChange={(selected) => setSelectedLanguage(selected?.value || '')}
                                options={languageSelectOptions}
                                placeholder="Select language"
                                isSearchable={false}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                            />
                        </div>

                        {/* Subtitle Format Selector (Softcode only) */}
                        {embedType === 'softcode' && currentView === 'options' && (
                            <div className="mb-6">
                                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Subtitle Format
                                </label>
                                <Select
                                    variant="modal"
                                    classNames={{
                                        control: () => '!rounded-lg border font-medium text-sm'
                                    }}
                                    value={withStyles ? {label: 'ASS', value: 'ass', badge: 'with styles'} : {label: 'SRT', value: 'srt', badge: 'plain text'}}
                                    onChange={(selected) => setWithStyles(selected?.value === 'ass')}
                                    options={[
                                        {
                                            label: 'ASS',
                                            value: 'ass',
                                            badge: 'with styles'
                                        },
                                        {
                                            label: 'SRT',
                                            value: 'srt',
                                            badge: 'plain text'
                                        }
                                    ]}
                                    formatOptionLabel={(option: any) => (
                                        <div className="flex items-center justify-between">
                                            <span>{option.label}</span>
                                            {option.badge && (
                                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                                    {option.badge}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    placeholder="Select format"
                                    isSearchable={false}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                />
                            </div>
                        )}

                        {/* Credit Cost */}
                        <div className="mb-5 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Credit Cost</span>
                                    {embedType === 'hardcode' && (
                                        <Popover
                                            trigger="hover"
                                            placement="top"
                                            content={
                                                <div className="p-3 w-64 text-sm text-gray-600 dark:text-gray-300">
                                                    Hardcode export uses {burnInRate} processing minute{burnInRate !== 1 ? 's' : ''} per minute of video.
                                                </div>
                                            }
                                        >
                                            <button type="button" className="ml-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                                <FontAwesomeIcon icon={faInfoCircle} className="w-3.5 h-3.5" />
                                            </button>
                                        </Popover>
                                    )}
                                </div>
                                <span className="text-gray-900 dark:text-gray-100">
                                    {embedType === 'softcode' ? (
                                        <span className="text-green-600 dark:text-green-400 font-medium">Free</span>
                                    ) : (
                                        formatMinutes(creditCost)
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Export Button */}
                        <div className="pt-2">
                            {embedType === 'softcode' ? (
                                <div>
                                    <PrimaryButton
                                        onClick={handleExport}
                                        className="w-full flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faArrowUpFromBracket} className="w-4 h-4 mr-2" />
                                        <span>Export</span>
                                    </PrimaryButton>
                                </div>
                            ) : (
                                <Popover
                                    trigger="hover"
                                    placement="top"
                                    disabled={!isFreeTrial}
                                    arrowClassName="!bg-gray-50 dark:!bg-gray-800"
                                    content={<FreeTrialPopover />}
                                >
                                    <div>
                                        <PrimaryButton
                                            onClick={handleContinueToUpload}
                                            disabled={!canUpload || isFreeTrial}
                                            className="w-full flex items-center justify-center"
                                        >
                                            <span>Continue</span>
                                            <FontAwesomeIcon className="text-white w-3 h-3 pl-2 align-middle" icon={faArrowRight}/>
                                        </PrimaryButton>
                                    </div>
                                </Popover>
                            )}
                        </div>
                    </div>

                    {/* View 1: Upload Progress View */}
                    <div
                        ref={el => viewRefs.current[1] = el}
                        className="w-full flex-shrink-0 px-7 pt-5 pb-5 transition-opacity duration-300"
                        style={{ opacity: currentView === 'upload_progress' ? 1 : 0 }}
                    >
                        <div className="flex flex-col min-h-[300px]">
                            {/* Upload progress in middle */}
                            <div className="flex-1 flex items-center justify-center">
                                <div className="w-full">
                                    <div className="flex items-center justify-center mb-2">
                                        <p className="text-center font-bold dark:text-gray-200">Uploading video</p>
                                    </div>
                                    <div className="text-center mb-6 text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2 flex-wrap">
                                        <span className="notranslate">{currentMediaFile?.name || 'Video file'}</span>
                                        {currentMediaFile?.size && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 notranslate">
                                                {bytesToSize(currentMediaFile.size)}
                                            </span>
                                        )}
                                    </div>
                                    <ProgressBar
                                        progress={uploadProgress * 100}
                                        height="md"
                                        color="blue"
                                    />
                                    <div className="flex items-center justify-between mt-3">
                                        <span className="text-gray-500 dark:text-gray-400 text-sm">
                                            {uploadEta !== undefined && uploadProgress < 1 && (() => {
                                                const {value, unit} = secondsToUnits(uploadEta);
                                                return (
                                                    <span>
                                                        <span className="notranslate">{value}</span> {unit} remaining
                                                    </span>
                                                );
                                            })()}
                                        </span>
                                        <span className="text-gray-900 dark:text-gray-100 text-sm font-medium">
                                            <span className="notranslate">{Math.round(uploadProgress * 100)}</span>% Complete
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Cancel button at bottom */}
                            <div className="mt-auto">
                                <button
                                    onClick={handleCancelUpload}
                                    className="w-full py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* View 2: Confirmation View */}
                    <div
                        ref={el => viewRefs.current[2] = el}
                        className="w-full flex-shrink-0 px-7 pt-5 pb-5 transition-opacity duration-300"
                        style={{ opacity: currentView === 'confirmation' ? 1 : 0 }}
                    >
                        <div className="flex flex-col min-h-[300px]">
                            {/* Confirmation info in middle */}
                            <div className="flex-1 flex items-center justify-center">
                                <div className="w-full">
                                    <div className="border-b border-gray-100 dark:border-gray-700 text-sm pb-3 mb-3 px-1 sm:px-3 flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400 text-base">Credits Used</span>
                                        <span className="font-bold dark:text-gray-200">{formatMinutes(Math.max(1, Math.ceil(creditCost)))}</span>
                                    </div>
                                    <div className="border-b border-gray-100 dark:border-gray-700 pb-3 mb-3 px-1 sm:px-3 text-sm flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400 mb-1 block text-base">Subtitle Language</span>
                                        <span className="font-bold dark:text-gray-200">{getLanguageName(selectedLanguage)}</span>
                                    </div>
                                    <div className="px-1 sm:px-3 text-sm flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400 mb-1 block text-base">Estimated Processing Time</span>
                                        <span className="font-bold dark:text-gray-200"><span>{estimatedProcessingDuration}</span><span> Minutes</span></span>
                                    </div>
                                </div>
                            </div>

                            {/* Buttons at bottom */}
                            <div className="mt-auto flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCurrentView('options')}
                                    disabled={isSubmitting}
                                    className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Back
                                </button>
                                <PrimaryButton
                                    className="flex-1 flex items-center justify-center"
                                    onClick={handleExport}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Spinner className="w-4 h-4 mr-2" />
                                            <span>Starting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faArrowUpFromBracket} className="w-4 h-4 mr-2" />
                                            <span>Export</span>
                                        </>
                                    )}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>

                    {/* View 3: Progress View (Both modes) */}
                    <div
                        ref={el => viewRefs.current[3] = el}
                        className="w-full flex-shrink-0 px-7 pt-5 pb-5 transition-opacity duration-300"
                        style={{ opacity: currentView === 'progress' ? 1 : 0 }}
                    >
                        <div className="flex flex-col min-h-[300px]">
                            {/* Progress in middle */}
                            <div className="flex-1 flex items-center justify-center">
                                <div className="w-full">
                                    <p className="text-center mb-4 font-bold dark:text-gray-200">Export in progress</p>
                                    <ProgressBar
                                        max={1}
                                        progress={embedType === 'softcode' ? (softcode.progress || 0) : burnInProgress}
                                        height="md"
                                        color="blue"
                                    />
                                    <span className="text-gray-500 dark:text-gray-400 mt-3 text-center block">
                                        <span className="notranslate">
                                            {embedType === 'softcode'
                                                ? ((softcode.progress || 0) * 100).toFixed()
                                                : (burnInProgress * 100).toFixed()}
                                        </span>% <span>Complete</span>
                                    </span>
                                </div>
                            </div>

                            {/* Button and info at bottom */}
                            <div className="mt-auto">
                                {embedType === 'hardcode' ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Closing this window will not cancel the export</p>
                                ) : (
                                    <button
                                        onClick={() => {
                                            softcode.handleCancel();
                                            setCurrentView('options');
                                        }}
                                        className="w-full py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function FreeTrialPopover() {
    return (
        <div className="px-4 py-2 w-72 bg-gray-50 dark:bg-gray-800">
            <span className="inline-block text-sm dark:text-gray-300">
                Hardcoding subtitles requires a <a className="text-blue-700 dark:text-blue-400 hover:underline" href={route('pricing')} target="_blank">paid account</a>. Purchase credits to unlock this feature.
            </span>
        </div>
    );
}
